import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion, useScroll } from 'framer-motion';
import { ArrowLeft, ArrowUpRight, Calendar, Clock, Share2 } from 'lucide-react';
import { Reveal, WordReveal } from '../components/Motion';
import { journalArticles, journalBySlug } from '../mock/journal';

const PROGRESS_STYLE_BASE = { background: 'var(--accent)', transformOrigin: '0 50%', width: '100%' };
const HERO_BLOB = { width: 400, height: 400, background: 'var(--accent)', top: '-20%', left: '60%', opacity: 0.10 };
const TAG_BG = { background: 'rgba(255,255,255,0.04)', border: '1px solid var(--line)' };
const SECTION_BG = { background: 'var(--bg-2)' };
const BORDER_LINE = { borderTop: '1px solid var(--line)' };
const BORDER_BOTTOM_LINE = { borderColor: 'var(--line)' };

const Block = ({ block, i }) => {
  if (block.type === 'h2') return <h2 className="h-display text-3xl md:text-4xl mt-14 mb-5">{block.text}</h2>;
  if (block.type === 'h3') return <h3 className="font-display text-xl md:text-2xl mt-10 mb-3">{block.text}</h3>;
  if (block.type === 'quote') return (
    <blockquote className="my-10 pl-6 border-l-2 border-accent">
      <p className="font-serif-i text-2xl md:text-3xl leading-snug">“{block.text}”</p>
    </blockquote>
  );
  return <p className="text-[16px] md:text-[17px] leading-[1.85] text-muted mb-5">{block.text}</p>;
};

const Renderer = ({ blocks }) => (
  <div className="prose-blocks">
    {blocks.map((b, i) => <Block key={`b${i}`} block={b} i={i} />)}
  </div>
);

const ReadingProgress = ({ progress }) => (
  <motion.div
    className="fixed top-[76px] left-0 h-[2px] z-40"
    style={{ ...PROGRESS_STYLE_BASE, scaleX: progress }}
  />
);

const JournalHeader = ({ article }) => (
  <section className="px-6 md:px-12 lg:px-20 pt-32 pb-16 relative overflow-hidden">
    <div className="blob" style={HERO_BLOB} />
    <div className="max-w-[860px] mx-auto relative">
      <Link to="/blog" className="text-[12px] uppercase tracking-widest text-dim inline-flex items-center gap-2 hover:text-accent">
        <ArrowLeft size={13} /> Back to journal
      </Link>
      <div className="mt-10 flex items-center gap-3 text-[11px] uppercase tracking-widest">
        <span className="text-accent">{article.cat}</span>
        <span className="text-dim">•</span>
        <span className="text-dim inline-flex items-center gap-1.5"><Calendar size={11} />{article.date}</span>
        <span className="text-dim">•</span>
        <span className="text-dim inline-flex items-center gap-1.5"><Clock size={11} />{article.read} read</span>
      </div>
      <h1 className="h-display mt-6 text-4xl md:text-6xl leading-[1.02]"><WordReveal text={article.title} /></h1>
      <p className="mt-6 font-serif-i text-xl md:text-2xl text-muted">{article.excerpt}</p>
    </div>
  </section>
);

const CoverImage = ({ cover, title }) => (
  <section className="px-6 md:px-12 lg:px-20 pb-16">
    <div className="max-w-[1100px] mx-auto aspect-[16/8] rounded-2xl overflow-hidden ring-glow">
      <img src={cover} alt={title} className="w-full h-full " />
    </div>
  </section>
);

const JournalContent = ({ article }) => (
  <article className="px-6 md:px-12 lg:px-20 pb-24">
    <div className="max-w-[760px] mx-auto">
      <Renderer blocks={article.body} />
      <div className="mt-16 pt-8 flex flex-wrap items-center justify-between gap-6" style={BORDER_LINE}>
        <div className="flex gap-2 flex-wrap">
          {article.tags.map((t) => (
            <span key={t} className="text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-full" style={TAG_BG}>{t}</span>
          ))}
        </div>
        <a href="#" className="link-arrow text-[13px]"><Share2 size={13} /> Share <span className="arrow"><ArrowUpRight size={13} /></span></a>
      </div>
    </div>
  </article>
);

const RelatedCard = ({ article, delay }) => (
  <Reveal delay={delay}>
    <Link to={`/blog/${article.slug}`} className="card-prem overflow-hidden group h-full block" data-cursor="hover">
      <div className="aspect-[16/10] overflow-hidden">
        <img src={article.cover} alt={article.title} className="w-full h-full  group-hover:scale-110 transition-transform duration-[1200ms]" />
      </div>
      <div className="p-6">
        <div className="flex items-center gap-3 text-[11px] uppercase tracking-widest text-dim">
          <span className="text-accent">{article.cat}</span><span>•</span><span>{article.date}</span>
        </div>
        <h3 className="font-display text-xl mt-4">{article.title}</h3>
      </div>
    </Link>
  </Reveal>
);

const RelatedArticles = ({ articles }) => (
  <section className="px-6 md:px-12 lg:px-20 py-24" style={SECTION_BG}>
    <div className="max-w-[1500px] mx-auto">
      <span className="section-eyebrow">Keep reading</span>
      <h2 className="h-display mt-6 text-4xl md:text-5xl">More from the journal.</h2>
      <div className="mt-12 grid md:grid-cols-3 gap-6">
        {articles.map((r, i) => <RelatedCard key={r.id} article={r} delay={i * 0.05} />)}
      </div>
    </div>
  </section>
);

export default function JournalDetail() {
  const { slug } = useParams();
  const article = journalBySlug(slug);
  const { scrollYProgress } = useScroll();
  if (!article) return <Navigate to="/blog" replace />;
  const related = journalArticles.filter((a) => a.slug !== slug).slice(0, 3);

  return (
    <div className="relative">
      <ReadingProgress progress={scrollYProgress} />
      <JournalHeader article={article} />
      <CoverImage cover={article.cover} title={article.title} />
      <JournalContent article={article} />
      <RelatedArticles articles={related} />
    </div>
  );
}
